/**
 * Presentation contract for the consent Audit log drawer.
 *
 * This module is the only seam between `getLeadAuditLog` and the two components
 * that render it. It holds the server's `category` tokens, the chip row, and the
 * icon/colour vocabulary; `mapAuditLogEntry.js` turns a raw row into the shape
 * `AuditLogItem` draws.
 *
 * Copy, ordering, icon glyphs and colours come from the Figma frame "Drawer"
 * (file NDVwkatBby4TFCN5oag0D1, node 27023-48954).
 */

/**
 * Server-side `category` argument values. Sent verbatim as the query variable —
 * these are the API's tokens, not display labels.
 */
export const AUDIT_LOG_CATEGORY = {
  SMS_CONSENT: "SMS_CONSENT",
  EMAIL_CONSENT: "EMAIL_CONSENT",
  OTTO_NUDGE: "OTTO_NUDGE",
  AUTO_RESPONDER: "AUTO_RESPONDER",
  OTTO_CAMPAIGN: "OTTO_CAMPAIGN",
};

/** Glyph to render, resolved to a component by AuditLogItem. */
export const AUDIT_LOG_ICON = {
  CHAT: "CHAT",
  MAIL: "MAIL",
  OTTO: "OTTO",
};

/**
 * Icon colours, as design tokens off the Figma inspect panel.
 * green Colors/Base/Green/6 · red Colors/Base/Red/5 ·
 * blue Colors/Brand/Primary/colorPrimary · amber Colors/Base/Orange/6 ·
 * grey Colors/Neutral/Icon/colorIcon
 */
export const AUDIT_LOG_ICON_COLOR = {
  GREEN: "#52C41A",
  RED: "#FF4D4F",
  BLUE: "#1677FF",
  AMBER: "#FA8C16",
  GREY: "rgba(0, 0, 0, 0.45)",
};

/**
 * Chip row, in design order — `langKey` resolves against `en.json`. The leading
 * "All" chip is ChipCheckboxGroup's own, so it is absent here; "All" is an empty
 * selection, which means the `category` variable is omitted from the query.
 */
export const AUDIT_LOG_FILTERS = [
  { langKey: "auditLogFilterSmsConsent", value: AUDIT_LOG_CATEGORY.SMS_CONSENT },
  { langKey: "auditLogFilterEmailConsent", value: AUDIT_LOG_CATEGORY.EMAIL_CONSENT },
  { langKey: "auditLogFilterOttoNudges", value: AUDIT_LOG_CATEGORY.OTTO_NUDGE },
  { langKey: "auditLogFilterAutoResponder", value: AUDIT_LOG_CATEGORY.AUTO_RESPONDER },
  { langKey: "auditLogFilterOttoCampaign", value: AUDIT_LOG_CATEGORY.OTTO_CAMPAIGN },
];

/** Rows per request. The drawer pages rather than infinite-scrolls. */
export const AUDIT_LOG_PAGE_SIZE = 20;

/**
 * Semantic tone of a resulting value, which drives the row's icon colour.
 * Derived from the new value, so a row reads as on/off at a glance.
 */
export const AUDIT_LOG_TONE = {
  POSITIVE: "POSITIVE", // opted in, enrolled, turned on
  NEGATIVE: "NEGATIVE", // opted out, declined
  NEUTRAL: "NEUTRAL", // turned off, disenrolled
  PENDING: "PENDING", // consent requested, implied
  WARNING: "WARNING", // paused
};

export const AUDIT_LOG_TONE_COLOR = {
  [AUDIT_LOG_TONE.POSITIVE]: AUDIT_LOG_ICON_COLOR.GREEN,
  [AUDIT_LOG_TONE.NEGATIVE]: AUDIT_LOG_ICON_COLOR.RED,
  [AUDIT_LOG_TONE.NEUTRAL]: AUDIT_LOG_ICON_COLOR.GREY,
  [AUDIT_LOG_TONE.PENDING]: AUDIT_LOG_ICON_COLOR.BLUE,
  [AUDIT_LOG_TONE.WARNING]: AUDIT_LOG_ICON_COLOR.AMBER,
};

/**
 * Status-dot colours for the in-thread consent pill (Figma node 27292-9591).
 * The pill's palette is not the drawer's: there a pause reads amber and a
 * disenrolment grey, here a pause is blue, a disenrolment amber, and a pending
 * request green ("Implied" — the lead may be messaged). Sampled off the design:
 * green Base/Green/5 · red Base/Red/5 · blue Base/Blue/5 · amber Base/Gold/6.
 */
export const AUDIT_LOG_DOT_COLOR = {
  [AUDIT_LOG_TONE.POSITIVE]: "#73D13D",
  [AUDIT_LOG_TONE.NEGATIVE]: "#FF4D4F",
  [AUDIT_LOG_TONE.NEUTRAL]: "#FAAD14",
  [AUDIT_LOG_TONE.PENDING]: "#73D13D",
  [AUDIT_LOG_TONE.WARNING]: "#4096FF",
};

/**
 * Per-category rendering rules.
 *
 * `fields` are the audit row's `changedFields` entries that belong to this
 * category — used to attribute a row when the drawer is on "All" and the server
 * therefore does not narrow by category. They are the lead columns behind the
 * Consents panel (`textConsentStatus`, `emailConsent`, `campaignAutoResponse`);
 * both snake_case and camelCase are accepted, so the lookup is normalised.
 *
 * `values` maps a stored value to `{ label, tone }`, rendered as
 * "{noun} {verb} {label}". A value whose sentence does not fit that frame gives
 * a full `title` instead. Anything not listed falls back to a humanised form of
 * the raw value with a neutral tone, so an unrecognised state still renders a
 * readable row instead of a blank one.
 *
 * The in-thread pill splits the same sentence into "{subject} ● {status}".
 * `pillNoun` (category or value) overrides the subject and `pillLabel` the
 * status where the drawer's wording is too long for a pill; a value with only a
 * `title` renders that title as the whole pill, with no status.
 */
export const AUDIT_LOG_CATEGORY_CONFIG = {
  [AUDIT_LOG_CATEGORY.SMS_CONSENT]: {
    icon: AUDIT_LOG_ICON.CHAT,
    noun: "SMS consent",
    verb: "set to",
    fields: ["text_consent_status", "sms_consent", "consent_status"],
    values: {
      ACCEPTED: { label: "Opted in", tone: AUDIT_LOG_TONE.POSITIVE },
      DECLINED: { label: "Opted out", tone: AUDIT_LOG_TONE.NEGATIVE },
      // Not "SMS consent set to Pending": a pending status means the request
      // went out and the lead is on implied consent until they answer.
      PENDING: {
        title: "SMS consent request sent (implied consent)",
        pillNoun: "SMS consent request sent",
        pillLabel: "Implied",
        tone: AUDIT_LOG_TONE.PENDING,
      },
      TRUE: { label: "Opted in", tone: AUDIT_LOG_TONE.POSITIVE },
      FALSE: { label: "Opted out", tone: AUDIT_LOG_TONE.NEGATIVE },
      // text_consent_status only goes back to null when the phone number on the
      // lead changes and the consent recorded against the old number is void.
      // "SMS consent set to Null" leaked the stored value; say why it reset.
      NULL: {
        title: "Phone number changed, SMS consent reset",
        tone: AUDIT_LOG_TONE.NEUTRAL,
      },
    },
  },
  [AUDIT_LOG_CATEGORY.EMAIL_CONSENT]: {
    icon: AUDIT_LOG_ICON.MAIL,
    noun: "Email",
    pillNoun: "Email consent",
    verb: "set to",
    fields: ["email_consent"],
    values: {
      TRUE: { label: "Opted in", tone: AUDIT_LOG_TONE.POSITIVE },
      FALSE: { label: "Opted out", tone: AUDIT_LOG_TONE.NEGATIVE },
      // email_consent is a nullable boolean: null = never recorded, which the
      // Consents panel reads as implied consent (EAI-1666). Keep it distinct
      // from false — collapsing the two would misreport an opt-out.
      NULL: { label: "Implied consent", pillLabel: "Implied", tone: AUDIT_LOG_TONE.PENDING },
    },
  },
  [AUDIT_LOG_CATEGORY.OTTO_NUDGE]: {
    icon: AUDIT_LOG_ICON.OTTO,
    noun: "Otto nudges",
    verb: "turned",
    fields: ["otto_nudge", "otto_nudge_enabled", "nudge_enabled", "is_nudge_enabled"],
    values: {
      TRUE: { label: "on", pillLabel: "On", tone: AUDIT_LOG_TONE.POSITIVE },
      FALSE: { label: "off", pillLabel: "Off", tone: AUDIT_LOG_TONE.NEUTRAL },
    },
  },
  [AUDIT_LOG_CATEGORY.AUTO_RESPONDER]: {
    icon: AUDIT_LOG_ICON.OTTO,
    // "Auto response", matching the Consents panel toggle (`autoResponseToggle`)
    // — the feature is not branded "Otto" anywhere the customer can see.
    noun: "Auto response",
    verb: "changed to",
    fields: ["campaign_auto_response", "auto_response", "auto_responder_status"],
    values: {
      ENROLLED: { label: "Enrolled", tone: AUDIT_LOG_TONE.POSITIVE },
      PAUSED: { label: "Paused", tone: AUDIT_LOG_TONE.WARNING },
      DISENROLLED: { label: "Disenrolled", tone: AUDIT_LOG_TONE.NEUTRAL },
    },
  },
  [AUDIT_LOG_CATEGORY.OTTO_CAMPAIGN]: {
    icon: AUDIT_LOG_ICON.OTTO,
    noun: "Otto campaign",
    verb: "turned",
    fields: ["otto_campaign", "campaign_enabled", "is_campaign_active", "campaign_status"],
    values: {
      TRUE: { label: "on", pillLabel: "On", tone: AUDIT_LOG_TONE.POSITIVE },
      FALSE: { label: "off", pillLabel: "Off", tone: AUDIT_LOG_TONE.NEUTRAL },
      ACTIVE: { label: "on", pillLabel: "On", tone: AUDIT_LOG_TONE.POSITIVE },
      INACTIVE: { label: "off", pillLabel: "Off", tone: AUDIT_LOG_TONE.NEUTRAL },
    },
  },
};

/**
 * Actor attribution (EAI-1807), applied identically by the drawer and the
 * in-thread row so the two surfaces cannot disagree about who did something:
 *
 *   - employee (USER)            → the username on the audit row
 *   - lead/customer (START/STOP) → the lead's own name
 *   - system/automation          → "EAI", or the external system's name when
 *                                  the row carries one
 *
 * `AUDIT_LOG_LEAD_ACTOR_TYPES` is the set of `updatedByType` values that mean
 * "the lead did this", and so resolve to the lead's name rather than to the
 * name recorded on the audit row.
 */
export const AUDIT_LOG_LEAD_ACTOR_TYPES = ["LEAD", "CUSTOMER"];

/**
 * `updatedByType` values that mean an employee did this, and so may be resolved
 * against the conversation thread's user records when the audit row itself
 * carries no name.
 */
export const AUDIT_LOG_EMPLOYEE_ACTOR_TYPES = ["USER"];

/**
 * `updatedByType` → who to name when no better name is available (system jobs
 * and inbound customer actions have no user record behind them).
 */
export const AUDIT_LOG_ACTOR_FALLBACK = {
  SYSTEM: "EAI",
  AUTOMATION: "EAI",
  OTTO: "EAI",
  LEAD: "Customer",
  CUSTOMER: "Customer",
  WEB_FORM: "Web form",
  USER: "User",
};

/** Used for an empty or unrecognised `updatedByType`. */
export const AUDIT_LOG_DEFAULT_ACTOR = "EAI";
